import { Injectable } from '@angular/core';
import { defer, delay, Observable, of } from 'rxjs';

import type { TableDataResponse } from '../../../../dashboard/services/dashboard-mock.service';
import type { TimelineEvent, TimelineEventSource, TimelineEventType } from '../models/timeline-event.model';
import type { User } from '../../../../../shared/models/user.model';
import type { VarianceIncident } from '../models/variance-incident.model';
import type { VarianceTableRow } from '../models/variance-table-row.model';
import type {
  Comment,
  CommentAuthor,
  CommentsServiceInterface,
} from '../../../../../shared/components/comments/models/comments-service.interface';
import { ActionAlertStatus } from '../../../models/employee-variance-tracker-row.model';
import { TableQuery } from '../../../../../shared/components/generic-table/models/table-query.model';
import { GenericTableColumn } from '../../../../../shared/components/generic-table/models/table-column.model';
import { getVarianceSupportingDataTableColumns } from '../constants/variance-supporting-data-table-columns.constants';
import {
  INCIDENT_STATUS_OPTIONS,
  type IncidentStatus,
} from '../../../../../shared/constants/incident-status-icons.constants';

export type { IncidentStatus };

@Injectable({ providedIn: 'root' })
export class VarianceSupportingDataMockService implements CommentsServiceInterface {
  private readonly incident: VarianceIncident = {
    id: 1,
    incidentId: 10003,
    medicationName: 'OXYCODONE',
    incidentNumber: '10003',
    employee: { name: 'ChuckH', id: 'CHU18' },
    patient: { name: 'Fernanda Rugama', id: 'FRMA2469' },
    status: 'Open',
  };

  private readonly tableRows: readonly VarianceTableRow[] = generateTableRows(10);

  private readonly users: readonly User[] = [
    {
      id: 1,
      username: 'BrooklynB',
      displayName: 'Brooklyn Benson',
      avatar: 'https://i.pravatar.cc/40?img=32',
      userId: '75628b77-9f3c-4998-b2b8-6b3d3d882941',
      firstName: null,
      lastName: null,
      email: '',
      cellNumber: null,
      midasAdmin: false,
      permissions: 0,
      employeeLevelId: null,
      otherEmployeeLevel: null,
      employeeTypeId: null,
      otherEmployeeType: null,
      normalizedUserRoleId: null,
      otherNormalizedUserRole: null,
      createdAt: '',
      lastAccessed: '',
      userGroups: [],
    },
    {
      id: 2,
      username: 'ArielM',
      displayName: 'Ariel Matamorros',
      avatar: 'https://i.pravatar.cc/40?img=47',
      userId: '54c1821f-44b2-4e64-bc4f-1548e0e32e7b',
      firstName: null,
      lastName: null,
      email: '',
      cellNumber: null,
      midasAdmin: false,
      permissions: 0,
      employeeLevelId: null,
      otherEmployeeLevel: null,
      employeeTypeId: null,
      otherEmployeeType: null,
      normalizedUserRoleId: null,
      otherNormalizedUserRole: null,
      createdAt: '',
      lastAccessed: '',
      userGroups: [],
    },
    {
      id: 3,
      username: 'PriscillaB',
      displayName: 'Priscilla Barack',
      avatar: 'https://i.pravatar.cc/40?img=11',
      userId: '78237503-5ce2-4246-9ee9-7f9642ffb3bb',
      firstName: null,
      lastName: null,
      email: '',
      cellNumber: null,
      midasAdmin: false,
      permissions: 0,
      employeeLevelId: null,
      otherEmployeeLevel: null,
      employeeTypeId: null,
      otherEmployeeType: null,
      normalizedUserRoleId: null,
      otherNormalizedUserRole: null,
      createdAt: '',
      lastAccessed: '',
      userGroups: [],
    },
    {
      id: 4,
      username: 'ChuckH',
      displayName: 'ChuckH',
      avatar: 'https://i.pravatar.cc/40?img=12',
      userId: 'e7a8995d-b60f-42e5-816c-e60eb7388a6b',
      firstName: null,
      lastName: null,
      email: '',
      cellNumber: null,
      midasAdmin: false,
      permissions: 0,
      employeeLevelId: null,
      otherEmployeeLevel: null,
      employeeTypeId: null,
      otherEmployeeType: null,
      normalizedUserRoleId: null,
      otherNormalizedUserRole: null,
      createdAt: '',
      lastAccessed: '',
      userGroups: [],
    },
    {
      id: 5,
      username: 'AnthonyM',
      displayName: 'AnthonyM',
      avatar: 'https://i.pravatar.cc/40?img=13',
      userId: '2b90d1af-fb09-4868-a64f-3b9872e9fa63',
      firstName: null,
      lastName: null,
      email: '',
      cellNumber: null,
      midasAdmin: false,
      permissions: 0,
      employeeLevelId: null,
      otherEmployeeLevel: null,
      employeeTypeId: null,
      otherEmployeeType: null,
      normalizedUserRoleId: null,
      otherNormalizedUserRole: null,
      createdAt: '',
      lastAccessed: '',
      userGroups: [],
    },
  ];

  private readonly comments: readonly Comment[] = [
    {
      id: '1',
      author: {
        id: 'u1',
        name: 'Brooklyn Benson',
        avatarUrl: 'https://i.pravatar.cc/40?img=32',
      },
      content: '@PriscillaB I changed the status as you requested!',
      taggedUsers: ['PriscillaB'],
      createdAt: new Date('2025-12-10T13:56:00'),
    },
    {
      id: '2',
      author: {
        id: 'u2',
        name: 'Priscilla Barack',
        avatarUrl: 'https://i.pravatar.cc/40?img=47',
      },
      content: 'Thanks all, case will be closed soon.',
      createdAt: new Date('2025-12-10T14:00:00'),
      taggedUsers: [],
    },
  ];

  getIncidentDetails(incidentNumber: string): Observable<VarianceIncident> {
    const incidentId = Number.parseInt(incidentNumber, 10) || this.incident.incidentId;
    const statusOptions = INCIDENT_STATUS_OPTIONS;
    const status: IncidentStatus = statusOptions[Math.abs(incidentId) % statusOptions.length]!.value;
    return of({
      ...this.incident,
      id: incidentId,
      incidentNumber: String(incidentNumber),
      incidentId,
      status,
    }).pipe(delay(150));
  }

  getTableData(query: TableQuery): Observable<TableDataResponse<VarianceTableRow>> {
    return defer(() => {
      const normalized = normalizeQuery(query);

      const filtered = applySearch(this.tableRows, normalized.search, (row) => [
        row.actionAlert || '',
        String(row.medAmount || ''),
        row.uom || '',
        row.transactionType || '',
        row.date || '',
        row.time || '',
        row.employee || '',
        row.employeeId || '',
        row.role || '',
        row.clinicalUnit || '',
        row.aiFlag || '',
        row.rxIntercept || '',
      ]);

      const sorted = applySort(filtered, normalized.sortField, normalized.sortOrder);

      const start = normalized.page * normalized.rows;
      const pageData = sorted.slice(start, start + normalized.rows);

      return of({
        data: pageData,
        totalRecords: sorted.length,
      } satisfies TableDataResponse<VarianceTableRow>);
    }).pipe(delay(250));
  }

  getUsersForTagging(search?: string): Observable<User[]> {
    return defer(() => {
      if (!search) {
        return of([...this.users]);
      }

      const term = search.toLowerCase();
      const filtered = this.users.filter(
        (u) => u.username.toLowerCase().includes(term) || u.displayName.toLowerCase().includes(term)
      );

      return of(filtered);
    }).pipe(delay(100));
  }

  getTimelineEvents(incidentId: string): Observable<TimelineEvent[]> {
    return defer(() => {
      const events = generateTimelineEvents(incidentId);
      return of(events);
    }).pipe(delay(200));
  }

  getTableColumns(): Observable<GenericTableColumn<VarianceTableRow>[]> {
    return of(getVarianceSupportingDataTableColumns()).pipe(delay(100));
  }

  getComments(incidentId: string): Observable<readonly Comment[]> {
    return of([...this.comments]).pipe(delay(150));
  }

  /** Placeholder GUID for comment author when userId is not set (API requires valid GUID). */
  private static readonly MOCK_AUTHOR_GUID = '00000000-0000-0000-0000-000000000001';

  getCurrentUser(): Observable<CommentAuthor> {
    const currentUser =
      this.users.find((u) => u.displayName === 'Priscilla Barack') || this.users[0];
    const id =
      (typeof currentUser.userId === 'string' && currentUser.userId.trim() !== '')
        ? currentUser.userId.trim()
        : VarianceSupportingDataMockService.MOCK_AUTHOR_GUID;
    return of({
      id,
      name: currentUser.displayName,
      avatarUrl: currentUser.avatar || 'https://i.pravatar.cc/40?img=11'
    } as const satisfies CommentAuthor).pipe(delay(50));
  }

}

function normalizeQuery(q: TableQuery): Required<Pick<TableQuery, 'page' | 'rows'>> & TableQuery {
  return {
    ...q,
    page: Math.max(0, q.page ?? 0),
    rows: Math.max(1, q.rows ?? 10),
  };
}

function applySearch<T>(
  rows: readonly T[],
  search: string | undefined,
  fields: (row: T) => readonly string[]
): T[] {
  const term = (search ?? '').trim().toLowerCase();
  if (!term) return [...rows];

  return rows.filter((r) => fields(r).some((v) => v.toLowerCase().includes(term)));
}

function applySort<T extends Record<string, unknown>>(
  rows: T[],
  sortField: string | undefined,
  sortOrder: 1 | -1 | undefined
): T[] {
  if (!sortField) return rows;
  const order: 1 | -1 = sortOrder === -1 ? -1 : 1;

  return [...rows].sort((a, b) => {
    const av = a[sortField];
    const bv = b[sortField];

    if (av == null && bv == null) return 0;
    if (av == null) return -1 * order;
    if (bv == null) return 1 * order;

    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * order;

    return String(av).localeCompare(String(bv)) * order;
  });
}

function generateTableRows(count: number): VarianceTableRow[] {
  const actionAlerts: readonly ActionAlertStatus[] = ['attention', 'none'];

  const transactionTypes = ['Remove', 'Charted', 'Waste'] as const;
  const employees = ['AnthonyM', 'ChuckH'] as const;
  const employeeIds = ['AMY76', 'CHU65'] as const;
  const roles = ['Nurse', 'Pharmacist'] as const;
  const clinicalUnits = ['H-Green'] as const;
  const aiFlags = ['No', 'Yes'] as const;
  const rxIntercepts = ['No'] as const;

  const testVideoUrl = '/videos/video-test.mp4';
  return Array.from({ length: count }, (_, i) => ({
    actionAlert: actionAlerts[i % actionAlerts.length],
    medAmount: i === 0 ? 10 : 2.5,
    uom: 'mg',
    transactionType: transactionTypes[i % transactionTypes.length],
    date: '10/02/2024',
    time: i === 0 ? '01:00 pm' : i === 1 ? '01:05 pm' : '01:10 pm',
    employee: employees[i % employees.length],
    employeeId: employeeIds[i % employees.length],
    role: roles[i % roles.length],
    clinicalUnit: clinicalUnits[0],
    thumbnail: '/thumbnail-template.png',
    videoUrl: testVideoUrl,
    aiFlag: aiFlags[i % aiFlags.length],
    rxIntercept: rxIntercepts[0],
  }));
}

function generateTimelineEvents(incidentId: string): TimelineEvent[] {
  const caseInvestigationId = Number.parseInt(incidentId, 10) || 0;
  return [
    {
      id: 1,
      message: `Feature Comparison deleted as Incident`,
      actorUserId: null,
      createdAt: new Date('2025-02-05T16:00:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 3, displayName: 'Priscilla Barack' },
      timelineEventTarget: { id: 1, timelineEventId: 1, caseInvestigationId },
      eventType: 'DeletionOfFeatureComparison' as TimelineEventType,
      eventSource: 'User' as TimelineEventSource,
    },
    {
      id: 2,
      message: 'New email response from email@example.com',
      actorUserId: null,
      createdAt: new Date('2025-02-13T08:16:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 1, displayName: 'Brooklyn Benson' },
      timelineEventTarget: { id: 2, timelineEventId: 2, caseInvestigationId },
      eventType: 'EmailResponse' as TimelineEventType,
      eventSource: 'User' as TimelineEventSource,
    },
    {
      id: 3,
      message: 'Incident sent as email to fer@example.com',
      actorUserId: null,
      createdAt: new Date('2025-02-13T08:15:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 1, displayName: 'Brooklyn Benson' },
      timelineEventTarget: { id: 3, timelineEventId: 3, caseInvestigationId },
      eventType: 'EmailNotification' as TimelineEventType,
      eventSource: 'User' as TimelineEventSource,
    },
    {
      id: 4,
      message:
        'New comment added: "@BrooklynB I changed the status as you requested, let me know if you need anything else."',
      actorUserId: null,
      createdAt: new Date('2025-02-12T12:53:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 3, displayName: 'Priscilla Barack' },
      timelineEventTarget: { id: 4, timelineEventId: 4, caseInvestigationId },
      eventType: 'NewComment' as TimelineEventType,
      eventSource: 'User' as TimelineEventSource,
    },
    {
      id: 5,
      message: 'Incident Status Changed to: Pending Response',
      actorUserId: null,
      createdAt: new Date('2025-02-12T23:58:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 3, displayName: 'Priscilla Barack' },
      timelineEventTarget: { id: 5, timelineEventId: 5, caseInvestigationId },
      eventType: 'StatusChange' as TimelineEventType,
      eventSource: 'User' as TimelineEventSource,
    },
  ].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()); // Newest first
}
